import { Suspense, lazy, useEffect } from 'react'

import ErrorBoundary from '@space-shell/common/components/errorBoundary'
import LoaderFull from '@space-shell/common/components/loaderFull'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'

import { CookieUtils } from '@space-shell/common/utils/cookie'

import { optimizelyLogConfig } from '@space-shell/common/utils/optimizely'

import { useAuthStore } from '@space-shell/core/store'
import { LoginPage, LogoutPage, RenewPage, SurrogatePage } from '@space-shell/pages/auth'
import { Routes, Route, BrowserRouter } from 'react-router-dom'

import { CommunityRouter } from './communityRouter'
import { BASE_PATH, RoutesPaths } from './constants'
import RouteGuard from './routeGuard'

const Home = lazy(() => import('@space-shell/pages/home'))
const Products = lazy(() => import('@space-shell/pages/products'))
const Gamification = lazy(() => import('@space-shell/pages/gamification'))
const SalesPage = lazy(() => import('@space-shell/pages/sales/'))
const ContentConsume = lazy(() => import('@space-shell/pages/contentConsume'))
const NotFound = lazy(() => import('@space-shell/pages/notFound'))
const ViewCertificate = lazy(() => import('@space-shell/pages/certificatePreview'))
const CertificateValidate = lazy(() => import('@space-shell/pages/certificateValidate'))
const CustomDomain = lazy(() => import('@space-shell/pages/customDomain'))
const CertificateValidatePublic = lazy(() => import('@space-shell/pages/certificateValidatePublic'))
const AccessPlus = lazy(() => import('@space-shell/pages/accessPlus'))

const STORAGE_KEY = 'optimizely.visitor_id'

/**
 * @component Deprecated Component
 *
 * @description
 * This component is deprecated and used only for standalone purpose.
 * It is just to handle authentication locally when running in standalone mode. This component is not used in production environments.
 *
 * @deprecated since version 1.93.0 - Use {@link remotes/clubServerSide/router/router.tsx} instead.
 *
 */
const Router = () => {
  const { authStatus } = useAuthContext()
  const { ucode } = useAuthStore()

  useEffect(() => {
    const setVisitorIdOnLocalStorage = () => {
      const visitorId =
        authStatus === AuthStatus.AUTHENTICATED
          ? ucode
          : CookieUtils.getCookieByKey(document.cookie, '_ga')

      localStorage.setItem(STORAGE_KEY, visitorId)
    }

    setVisitorIdOnLocalStorage()
    optimizelyLogConfig()
  }, [authStatus, ucode])

  return (
    <BrowserRouter>
      <Suspense fallback={<LoaderFull />}>
        <ErrorBoundary>
          <Routes>
            <Route path={`${RoutesPaths.HOME}${RoutesPaths.AUTH_LOGIN}`} element={<LoginPage />} />
            <Route
              path={`${RoutesPaths.HOME}${RoutesPaths.AUTH_LOGOUT}`}
              element={<LogoutPage />}
            />
            <Route path={`${RoutesPaths.HOME}${RoutesPaths.AUTH_RENEW}`} element={<RenewPage />} />
            <Route path={`${BASE_PATH}${RoutesPaths.AUTH_SURROGATE}`} element={<SurrogatePage />} />
            <Route path={RoutesPaths.HOME} element={<RouteGuard />}>
              <Route index element={<Home />} />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.ACCESS_PLUS}`}
                element={<AccessPlus />}
              />
              <Route path={`${RoutesPaths.HOME}${RoutesPaths.PRODUCTS}`} element={<Products />} />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.GAMIFICATION}`}
                element={<Gamification />}
              />

              <Route path={`${RoutesPaths.HOME}${RoutesPaths.SALES}`} element={<SalesPage />} />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.CONTENT_CONSUME}`}
                element={<ContentConsume />}
              />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.COMMUNITY}/*`}
                element={<CommunityRouter />}
              />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.CERTIFICATE_PREVIEW}`}
                element={<ViewCertificate />}
              />

              <Route
                path={`${RoutesPaths.HOME}${RoutesPaths.CERTIFICATE_VALIDATE}`}
                element={<CertificateValidate />}
              />
            </Route>

            <Route
              path={`${BASE_PATH}${RoutesPaths.CERTIFICATE_VALIDATE}`}
              element={<CertificateValidatePublic />}
            />

            <Route path={`${BASE_PATH}/*`} element={<NotFound />} />

            <Route path={'/'} element={<CustomDomain />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </BrowserRouter>
  )
}

export default Router
